<template>
  <el-dialog
      :model-value="modelValue"
      title="两步验证设置"
      width="560px"
      :close-on-click-modal="false"
      @update:model-value="(val) => emit('update:modelValue', val)"
      @open="loadStatus"
  >
    <div v-loading="loading" class="two-factor-settings">

      <!-- 系统级开关提示 -->
      <el-alert
          v-if="status && !status.system_two_factor_enabled"
          title="系统管理员已禁用两步验证功能，当前无法开启"
          type="warning"
          :closable="false"
          show-icon
          class="sys-alert"
      />

      <!-- 两步验证总开关 -->
      <div class="setting-item">
        <div class="item-info">
          <div class="item-title">两步验证</div>
          <div class="item-desc">
            开启后，每次登录需在密码校验通过后完成二次验证。
            邮箱可用时优先发送邮箱验证码，邮箱不可用时使用已绑定的验证器动态码。
          </div>
        </div>
        <el-switch
            :model-value="status?.two_factor_enabled || false"
            :loading="toggling"
            :disabled="!status || (status && !status.system_two_factor_enabled && !status.two_factor_enabled)"
            @change="onToggleTwoFactor"
        />
      </div>

      <!-- 验证器绑定管理 -->
      <div class="setting-item">
        <div class="item-info">
          <div class="item-title">
            验证器（Aegis Authenticator）
            <el-tag v-if="status?.authenticator_enabled" type="success" size="small">已绑定</el-tag>
            <el-tag v-else type="info" size="small">未绑定</el-tag>
          </div>
          <div class="item-desc">
            <template v-if="status?.authenticator_enabled">
              已于 {{ formatBoundAt(status.authenticator_bound_at) }} 绑定。
              绑定后邮箱不可用时，登录可用验证器动态码完成二次验证。
            </template>
            <template v-else>
              绑定兼容 TOTP 协议的验证器 App（Aegis Authenticator / Google Authenticator 等），
              扫码或手动录入密钥后输入动态码完成绑定。
            </template>
          </div>
        </div>
        <div class="item-actions">
          <el-button
              v-if="!status?.authenticator_enabled"
              size="small"
              type="primary"
              plain
              :loading="binding"
              @click="startBind"
          >
            绑定验证器
          </el-button>
          <el-button
              v-else
              size="small"
              type="danger"
              plain
              @click="unbindVisible = true"
          >
            解绑验证器
          </el-button>
        </div>
      </div>

      <!-- 绑定验证器：二维码展示区 -->
      <div v-if="bindData" class="bind-panel">
        <el-alert
            title="请使用手机验证器 App 扫描二维码或手动录入密钥，然后输入 App 显示的 6 位动态码完成绑定"
            type="info"
            :closable="false"
            show-icon
        />
        <div class="qr-wrap">
          <img :src="bindData.qr_code" alt="验证器二维码" class="qr-image"/>
          <div class="secret-wrap">
            <div class="secret-label">手动录入密钥（Base32）</div>
            <div class="secret-value">{{ bindData.secret }}</div>
            <el-button size="small" text type="primary" @click="copySecret">
              复制密钥
            </el-button>
            <div class="secret-meta">
              <div>签发方：{{ bindData.issuer }}</div>
              <div>账户：{{ bindData.account }}</div>
            </div>
          </div>
        </div>
        <div class="bind-code-row">
          <el-input
              v-model="bindCode"
              placeholder="请输入验证器 6 位动态码"
              maxlength="10"
              @keyup.enter="confirmBind"
          />
          <el-button type="primary" :loading="binding" @click="confirmBind">
            确认绑定
          </el-button>
        </div>
      </div>

      <!-- 解绑验证器：输入动态码 -->
      <div v-if="unbindVisible" class="bind-panel">
        <el-alert
            title="解绑需输入当前有效动态码，防止账号被盗后绕过两步验证"
            type="warning"
            :closable="false"
            show-icon
        />
        <div class="bind-code-row">
          <el-input
              v-model="unbindCode"
              placeholder="请输入验证器 6 位动态码"
              maxlength="10"
              @keyup.enter="confirmUnbind"
          />
          <el-button type="danger" :loading="unbinding" @click="confirmUnbind">
            确认解绑
          </el-button>
        </div>
      </div>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="emit('update:modelValue', false)">关闭</el-button>
      </span>
    </template>

    <!-- 关闭两步验证：身份确认对话框 -->
    <el-dialog
        v-model="disableDialogVisible"
        title="关闭两步验证 - 身份验证"
        width="440px"
        append-to-body
        :close-on-click-modal="false"
    >
      <el-form label-width="90px">
        <el-form-item label="验证方式">
          <el-radio-group v-model="disableMethod" @change="disableCode = ''">
            <el-radio-button v-if="status?.email_available" label="email">
              邮箱验证码
            </el-radio-button>
            <el-radio-button v-if="status?.authenticator_enabled" label="authenticator">
              验证器动态码
            </el-radio-button>
            <el-radio-button label="password">登录密码</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="disableMethod === 'email'" label="验证码">
          <div class="code-row">
            <el-input
                v-model="disableCode"
                placeholder="请输入6位验证码"
                maxlength="10"
            />
            <el-button
                :disabled="countdown > 0"
                :loading="sendingCode"
                @click="sendDisableCode"
            >
              {{ countdown > 0 ? `${countdown}秒后重发` : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>

        <el-form-item
            v-else-if="disableMethod === 'authenticator'"
            label="动态码"
        >
          <el-input
              v-model="disableCode"
              placeholder="请输入验证器6位动态码"
              maxlength="10"
          />
        </el-form-item>

        <el-form-item v-else label="登录密码">
          <el-input
              v-model="disableCode"
              type="password"
              placeholder="请输入登录密码"
              show-password
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="disableDialogVisible = false">取消</el-button>
          <el-button type="danger" :loading="toggling" @click="confirmDisable">
            确认关闭
          </el-button>
        </span>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script setup>
import {onBeforeUnmount, ref} from 'vue'
import {ElMessage} from 'element-plus'
import dayjs from 'dayjs'
import {
  bindAuthenticator,
  confirmBindAuthenticator,
  disableTwoFactor,
  enableTwoFactor,
  getTwoFactorStatus,
  sendTwoFactorEmail,
  unbindAuthenticator
} from '@/api/auth'

defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const loading = ref(false)
const toggling = ref(false)
const binding = ref(false)
const unbinding = ref(false)
const sendingCode = ref(false)
const status = ref(null)

// 绑定验证器流程
const bindData = ref(null)
const bindCode = ref('')

// 解绑验证器流程
const unbindVisible = ref(false)
const unbindCode = ref('')

// 关闭两步验证流程
const disableDialogVisible = ref(false)
const disableMethod = ref('email')
const disableCode = ref('')
const countdown = ref(0)
let countdownTimer = null

const extractError = (error, fallback) => {
  return error.response?.data?.message ||
      error.response?.data?.detail ||
      error.response?.data?.error ||
      error.message ||
      fallback
}

const formatBoundAt = (time) => {
  if (!time) return '-'
  return dayjs(time).format('YYYY-MM-DD HH:mm')
}

const loadStatus = async () => {
  try {
    loading.value = true
    const response = await getTwoFactorStatus()
    status.value = response?.data || response || null

    // 默认关闭方式：邮箱可用优先邮箱，其次验证器，最后密码
    if (status.value) {
      if (status.value.email_available) {
        disableMethod.value = 'email'
      } else if (status.value.authenticator_enabled) {
        disableMethod.value = 'authenticator'
      } else {
        disableMethod.value = 'password'
      }
    }
  } catch (error) {
    console.error('加载两步验证状态失败:', error)
    ElMessage.error(extractError(error, '加载两步验证状态失败'))
  } finally {
    loading.value = false
  }
}

// 开启/关闭两步验证总开关
const onToggleTwoFactor = async (value) => {
  if (value) {
    // 开启
    try {
      toggling.value = true
      const response = await enableTwoFactor()
      ElMessage.success(response?.data?.message || response?.message || '两步验证已开启')
      await loadStatus()
    } catch (error) {
      console.error('开启两步验证失败:', error)
      ElMessage.error(extractError(error, '开启两步验证失败'))
    } finally {
      toggling.value = false
    }
  } else {
    // 关闭：先弹身份验证对话框
    disableCode.value = ''
    disableDialogVisible.value = true
  }
}

// 关闭两步验证时发送邮箱验证码（登录态，不带 mfa_ticket）
const sendDisableCode = async () => {
  try {
    sendingCode.value = true
    const response = await sendTwoFactorEmail()
    const data = response?.data || {}
    ElMessage.success(data.message || '验证码已发送，请查收邮箱')

    const resendAfter = data.resend_after || 60
    countdown.value = resendAfter
    clearInterval(countdownTimer)
    countdownTimer = setInterval(() => {
      countdown.value -= 1
      if (countdown.value <= 0) {
        clearInterval(countdownTimer)
        countdownTimer = null
      }
    }, 1000)
  } catch (error) {
    console.error('发送验证码失败:', error)
    ElMessage.error(extractError(error, '验证码发送失败'))
  } finally {
    sendingCode.value = false
  }
}

const confirmDisable = async () => {
  if (!disableCode.value.trim()) {
    ElMessage.warning('请输入验证码/动态码/登录密码')
    return
  }

  try {
    toggling.value = true
    const response = await disableTwoFactor({
      method: disableMethod.value,
      code: disableCode.value.trim()
    })
    ElMessage.success(response?.data?.message || response?.message || '两步验证已关闭')
    disableDialogVisible.value = false
    await loadStatus()
  } catch (error) {
    console.error('关闭两步验证失败:', error)
    ElMessage.error(extractError(error, '关闭两步验证失败'))
  } finally {
    toggling.value = false
  }
}

// 开始绑定验证器
const startBind = async () => {
  try {
    binding.value = true
    bindCode.value = ''
    const response = await bindAuthenticator()
    bindData.value = response?.data || response || null
    if (!bindData.value?.qr_code) {
      ElMessage.error('获取二维码失败，请重试')
      bindData.value = null
    }
  } catch (error) {
    console.error('开始绑定验证器失败:', error)
    ElMessage.error(extractError(error, '开始绑定验证器失败'))
  } finally {
    binding.value = false
  }
}

const copySecret = async () => {
  if (!bindData.value?.secret) return
  try {
    await navigator.clipboard.writeText(bindData.value.secret)
    ElMessage.success('密钥已复制')
  } catch (error) {
    // 剪贴板不可用时降级提示
    ElMessage.info('请手动复制密钥')
  }
}

// 确认绑定验证器
const confirmBind = async () => {
  if (!bindCode.value.trim()) {
    ElMessage.warning('请输入验证器动态码')
    return
  }

  try {
    binding.value = true
    const response = await confirmBindAuthenticator(bindCode.value.trim())
    ElMessage.success(response?.data?.message || response?.message || '验证器绑定成功')
    bindData.value = null
    bindCode.value = ''
    await loadStatus()
  } catch (error) {
    console.error('确认绑定失败:', error)
    ElMessage.error(extractError(error, '确认绑定失败'))
  } finally {
    binding.value = false
  }
}

// 解绑验证器
const confirmUnbind = async () => {
  if (!unbindCode.value.trim()) {
    ElMessage.warning('请输入验证器动态码')
    return
  }

  try {
    unbinding.value = true
    const response = await unbindAuthenticator(unbindCode.value.trim())
    ElMessage.success(response?.data?.message || response?.message || '验证器已解绑')
    unbindVisible.value = false
    unbindCode.value = ''
    await loadStatus()
  } catch (error) {
    console.error('解绑验证器失败:', error)
    ElMessage.error(extractError(error, '解绑验证器失败'))
  } finally {
    unbinding.value = false
  }
}

onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
})
</script>

<style scoped lang="scss">
.sys-alert {
  margin-bottom: 16px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;

  .item-info {
    flex: 1;
    margin-right: 16px;

    .item-title {
      font-weight: 600;
      color: #303133;
      margin-bottom: 6px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .item-desc {
      font-size: 13px;
      color: #909399;
      line-height: 1.6;
    }
  }

  .item-actions {
    flex-shrink: 0;
  }
}

.bind-panel {
  margin-top: 16px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 6px;

  .qr-wrap {
    display: flex;
    gap: 20px;
    margin: 16px 0;
    align-items: center;

    .qr-image {
      width: 160px;
      height: 160px;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      background: #fff;
    }

    .secret-wrap {
      flex: 1;

      .secret-label {
        font-size: 12px;
        color: #909399;
        margin-bottom: 6px;
      }

      .secret-value {
        font-family: monospace;
        font-size: 14px;
        color: #303133;
        background: #fff;
        border: 1px dashed #dcdfe6;
        border-radius: 4px;
        padding: 8px;
        word-break: break-all;
        margin-bottom: 8px;
      }

      .secret-meta {
        margin-top: 8px;
        font-size: 12px;
        color: #909399;
        line-height: 1.8;
      }
    }
  }

  .bind-code-row {
    display: flex;
    gap: 10px;

    .el-input {
      flex: 1;
    }
  }
}

.code-row {
  display: flex;
  gap: 10px;
  width: 100%;

  .el-input {
    flex: 1;
  }
}
</style>
